import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

// Default configuration
const DEFAULT_CONFIG = {
  tokenAddress: null,
  tokenSymbol: null,
  poolAddress: null,
  loading: true,
  error: null
};

const TokenConfigContext = createContext(DEFAULT_CONFIG);

export const TokenConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  const loadConfig = useCallback(async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/token-config`);
      
      if (response.data.success && response.data.config) {
        const cfg = response.data.config;
        if (!cfg.token_address || !cfg.token_symbol || !cfg.pool_address || !cfg.instance_id) {
          throw new Error('Неполная конфигурация сервера');
        }
        setConfig({
          instanceId: cfg.instance_id,
          tokenAddress: cfg.token_address,
          tokenSymbol: cfg.token_symbol,
          poolAddress: cfg.pool_address || null,
          updatedAt: cfg.updated_at,
          loading: false,
          error: null
        });
        console.log('✅ Token config loaded:', cfg.token_symbol);
      } else {
        throw new Error('Сервер не вернул конфигурацию токена');
      }
    } catch (error) {
      console.error('Error loading token config:', error);
      setConfig(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message 
      }));
    }
  }, []);

  const updateConfig = useCallback(async (tokenAddress, tokenSymbol, poolAddress = null) => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.put(`${API_URL}/api/token-config`, {
        token_address: tokenAddress,
        token_symbol: tokenSymbol,
        pool_address: poolAddress
      });
      
      if (response.data.success && response.data.config) {
        const cfg = response.data.config;
        if (!cfg.token_address || !cfg.token_symbol || !cfg.pool_address || !cfg.instance_id) {
          throw new Error('Неполная конфигурация сервера');
        }
        setConfig({
          instanceId: cfg.instance_id,
          tokenAddress: cfg.token_address,
          tokenSymbol: cfg.token_symbol,
          poolAddress: cfg.pool_address,
          updatedAt: cfg.updated_at,
          loading: false,
          error: null
        });
        return { success: true, config: cfg };
      }
      
      return { success: false, error: 'Failed to update config' };
    } catch (error) {
      console.error('Error updating token config:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message 
      };
    }
  }, []);

  const verifyToken = useCallback(async (tokenAddress) => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/token-config/verify`, {
        token_address: tokenAddress
      });
      
      return response.data;
    } catch (error) {
      console.error('Error verifying token:', error);
      return { 
        success: false, 
        valid: false,
        error: error.response?.data?.detail || error.message 
      };
    }
  }, []);

  const refresh = useCallback(() => {
    loadConfig();
  }, [loadConfig]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadConfig, 60000);
    return () => clearInterval(interval);
  }, [loadConfig]);

  // Update page title when token symbol changes
  useEffect(() => {
    if (config.tokenSymbol && !config.loading) {
      document.title = `${config.tokenSymbol} | Market Maker`;
    }
  }, [config.tokenSymbol, config.loading]);

  const value = {
    ...config,
    refresh,
    updateConfig,
    verifyToken
  };

  return (
    <TokenConfigContext.Provider value={value}>
      {config.loading || config.error ? (
        <div role="status" className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
          <p>{config.error ? `Конфигурация недоступна: ${config.error}` : 'Загрузка конфигурации сервера…'}</p>
          {config.error && <button onClick={refresh}>Повторить</button>}
        </div>
      ) : <React.Fragment key={`${config.instanceId}:${config.tokenAddress}`}>{children}</React.Fragment>}
    </TokenConfigContext.Provider>
  );
};

export const useTokenConfig = () => {
  const context = useContext(TokenConfigContext);
  if (!context) {
    throw new Error('useTokenConfig must be used within a TokenConfigProvider');
  }
  return context;
};

export default TokenConfigContext;



